/**
 * Streak compute with freeze + TM coverage.
 *
 * Wraps `calculateGlobalStreak` so callers don't have to manually thread the
 * `savedDays` set. Reconciles new freeze events when invoked from a write-side
 * context (e.g. level completion); read-only views can skip reconciliation.
 */

import type { DailyActivity, GlobalStreak } from '../types/gamification';
import type { FreezeEvent } from '../types/store';
import { calculateGlobalStreak } from './globalStreakUtils';
import { loadSavedDays } from '../services/StoreStateService';
import { reconcileFreezeEvents } from './freezeReconciler';

interface ComputeOptions {
  reconcile?: boolean;
  onNewFreezeEvent?: (event: FreezeEvent) => void;
}

// Version suffix: bump whenever the reconciler's rules change, so the
// once-per-day marker doesn't stop existing users from getting one re-run
// under the new rules. v2 = purchase-day-inclusive gate + per-type refusals.
// v3 = weekend freezes no longer spend coverage on a day still in progress,
// and the weekend window is read without a silent sat-sun fallback.
// v4 = force-resync now clears the marker for real. Clients that set a v3
// marker today did so under the build that couldn't clear it, and their
// force-resync token is already consumed — so they'd stay gated until
// tomorrow. A fresh version gives them their re-run now.
const RECONCILE_MARKER_PREFIX = 'codeLeague:reconciled:';
const RECONCILE_MARKER_KEY = (userId: string) => `${RECONCILE_MARKER_PREFIX}v4:${userId}`;

/**
 * Drop the once-per-day marker for EVERY version. Callers outside this module
 * can't know which version wrote the current marker, and guessing the key is
 * how admin force-resyncs silently stopped forcing a reconcile: they removed
 * an unversioned key that no build has ever written.
 */
export function clearReconcileMarkers(userId: string): void {
  const suffix = `:${userId}`;
  const stale = Object.keys(localStorage).filter(
    key => key.startsWith(RECONCILE_MARKER_PREFIX) && key.endsWith(suffix),
  );
  for (const key of stale) localStorage.removeItem(key);
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Force the next computeStreakWithFreezes call with reconcile:true to actually
 * run the reconciler, even if it already ran today. Call after a freeze
 * purchase so the new inventory can fire on the next level completion.
 */
export function invalidateReconcileMarker(userId: string): void {
  clearReconcileMarkers(userId);
}

export async function computeStreakWithFreezes(
  userId: string,
  dailyActivities: DailyActivity[],
  requiredLevels: number,
  options: ComputeOptions = {},
): Promise<GlobalStreak> {
  if (options.reconcile) {
    const today = todayYmd();
    const marker = localStorage.getItem(RECONCILE_MARKER_KEY(userId));
    if (marker !== today) {
      try {
        const { newFired, newRefused } = await reconcileFreezeEvents(
          userId,
          dailyActivities,
          requiredLevels,
        );
        localStorage.setItem(RECONCILE_MARKER_KEY(userId), today);
        if (options.onNewFreezeEvent) {
          for (const e of [...newFired, ...newRefused]) {
            options.onNewFreezeEvent(e);
          }
        }
        if (newFired.length > 0 || newRefused.length > 0) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('progressUpdated'));
          }
        }
      } catch (error) {
        console.error('Freeze reconciliation failed (continuing without):', error);
      }
    }
  }
  const savedDays = await loadSavedDays(userId);
  return calculateGlobalStreak(dailyActivities, requiredLevels, savedDays);
}
